import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from "firebase/auth";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { ensureFirebaseAuth, getFirebaseAuth } from "@/lib/firebase";
import { log, maskEmail } from "@/lib/logger";

interface AuthValue {
  user: User | null;
  uid: string | null;
  loading: boolean;
  configured: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  logOut: () => Promise<void>;
  /** Re-sends the Firebase verification email to the signed-in doctor. */
  resendVerification: () => Promise<void>;
  emailVerified: boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(false);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    void ensureFirebaseAuth().then((auth) => {
      if (cancelled) return;
      setConfigured(Boolean(auth));
      if (!auth) {
        log.warn("auth", "not.configured", { msg: "VITE_FIREBASE_API_KEY is unset" });
        setLoading(false);
        return;
      }
      unsubscribe = onAuthStateChanged(auth, (next) => {
        log.info("auth", "state.changed", {
          uid: next?.uid ?? null,
          emailVerified: next?.emailVerified ?? null,
          signedIn: Boolean(next),
        });
        setUser(next);
        setLoading(false);
      });
    });
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      uid: user?.uid ?? null,
      loading,
      configured,
      signIn: async (email, password) => {
        const done = log.start("auth", "signin", { email: maskEmail(email) });
        const auth = await ensureFirebaseAuth();
        if (!auth) {
          done({ level: "error", event: "signin.unconfigured" });
          throw new Error("Authentication is not configured yet.");
        }
        try {
          const credential = await signInWithEmailAndPassword(auth, email, password);
          done({ uid: credential.user.uid });
        } catch (error) {
          done({ level: "warn", event: "signin.failed", code: (error as { code?: string }).code });
          throw error;
        }
      },
      signUp: async (email, password) => {
        const done = log.start("auth", "signup", { email: maskEmail(email) });
        const auth = await ensureFirebaseAuth();
        if (!auth) {
          done({ level: "error", event: "signup.unconfigured" });
          throw new Error("Authentication is not configured yet.");
        }
        let credential;
        try {
          credential = await createUserWithEmailAndPassword(auth, email, password);
        } catch (error) {
          done({ level: "warn", event: "signup.failed", code: (error as { code?: string }).code });
          throw error;
        }
        // Email verification is part of the doctor onboarding flow.
        try {
          await sendEmailVerification(credential.user);
          done({ uid: credential.user.uid, verificationSent: true });
        } catch (error) {
          // Non-fatal: the doctor can resend from the registration screen.
          log.warn("auth", "verification.send.failed", {
            uid: credential.user.uid,
            code: (error as { code?: string }).code,
          });
          done({ uid: credential.user.uid, verificationSent: false });
        }
      },
      emailVerified: Boolean(user?.emailVerified),
      resendVerification: async () => {
        const auth = await ensureFirebaseAuth();
        if (!auth?.currentUser) throw new Error("You need to be logged in.");
        await sendEmailVerification(auth.currentUser);
        log.info("auth", "verification.resent", { uid: auth.currentUser.uid });
      },
      logOut: async () => {
        const auth = getFirebaseAuth();
        if (auth) {
          const uid = auth.currentUser?.uid ?? null;
          await signOut(auth);
          log.info("auth", "signout", { uid });
        }
      },
    }),
    [user, loading, configured],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
